import type { UIMessage } from "ai";
import { Check, CircleAlert, FileText, PenLine, Sparkles, Tag } from "lucide-react";
import { cn } from "@/lib/cn";
import type { Section } from "@/lib/briefs";
import { TECHNIQUES } from "@/lib/chat";
import { Mockup } from "@/components/Mockup";
import { Prose } from "@/components/Prose";
import { mockupsOf, type Reactions } from "@/lib/cards";

type Part = UIMessage["parts"][number];

interface ToolPart {
  type: string;
  toolCallId: string;
  state: string;
  input?: unknown;
  output?: unknown;
}

function isTool(p: Part): p is Part & ToolPart {
  return typeof p.type === "string" && p.type.startsWith("tool-") && "toolCallId" in p;
}

/** The AI names boxes 3 and 4 `want` and `have`; older turns used the stored keys. */
const MODEL_KEYS: Record<string, string> = { want: "dish", have: "ingredients" };

function sectionOf(sections: Section[], key: unknown): Section | null {
  if (typeof key !== "string") return null;
  const raw = key.trim().toLowerCase();
  const k = MODEL_KEYS[raw] ?? raw;
  return sections.find((x) => (x.coreKey ?? `custom:${x.id}`).toLowerCase() === k || x.title.toLowerCase() === raw) ?? null;
}

function sectionTitle(sections: Section[], key: unknown): string {
  if (typeof key !== "string") return "a box";
  return sectionOf(sections, key)?.title ?? key;
}

function failedPart(part: ToolPart): boolean {
  const output = part.output;
  return part.state === "output-error" || (!!output && typeof output === "object" && "error" in output);
}

/** Which box or brief field a tool call aims at, so a failure can be matched to a success in the same turn. */
function targetOf(part: ToolPart): string {
  const input = (part.input ?? {}) as Record<string, unknown>;
  const key = typeof input.section === "string" ? input.section.trim().toLowerCase() : "";
  return `${part.type}:${MODEL_KEYS[key] ?? key}`;
}

/**
 * A tool call that did not do what it was asked, in plain words. A failed
 * write is a card for the person who can edit: what did not happen, that their
 * text is safe, and the ways on (open the box; ask for it shorter, only when it
 * was too long for the box). The
 * buttons hide while the chat is busy. Read-only views get the plain line.
 */
function ToolFailure({
  part,
  sections,
  canEdit,
  busy,
  onOpen,
  onShorten,
}: {
  part: ToolPart;
  sections: Section[];
  canEdit: boolean;
  busy: boolean;
  onOpen?: (sectionId: string) => void;
  onShorten?: (sectionId: string | null) => void;
}) {
  const name = part.type.slice("tool-".length);
  const input = (part.input ?? {}) as Record<string, unknown>;
  const box = sectionTitle(sections, input.section);
  const target = sectionOf(sections, input.section);
  const line = (what: string, todo: string) => (
    <p className="text-destructive flex items-start gap-1.5 text-xs">
      <CircleAlert size={13} className="mt-px shrink-0" />
      <span>{canEdit ? `${what} ${todo}` : what}</span>
    </p>
  );

  switch (name) {
    case "write_section": {
      const title = `Couldn't save this to ${box}.`;
      if (!canEdit) return line(title, "");
      const open = onOpen && target ? onOpen : null;
      // Shorter helps only a write too long for the box; one that would remove
      // text, or was rejected, is for the person to change in the box.
      const output = part.output as { error?: unknown } | undefined;
      const tooLong = typeof output?.error === "string" && output.error.startsWith("Too long");
      const shorten = tooLong ? onShorten : undefined;
      return (
        <div role="status" className="border-destructive/30 bg-destructive/5 rounded-lg border px-3 py-2 text-sm">
          <p className="text-destructive flex items-start gap-1.5 font-medium">
            <CircleAlert size={14} className="mt-0.5 shrink-0" />
            <span>{title}</span>
          </p>
          <p className="text-muted-foreground mt-0.5 text-xs">Your earlier text is safe.</p>
          {!busy && (open || shorten) ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {open && target ? (
                <button
                  type="button"
                  onClick={() => open(target.id)}
                  className="border-border bg-card hover:bg-accent focus-visible:outline-ring rounded-full border px-3 py-1 text-xs font-medium focus-visible:outline-2"
                >
                  Open the box
                </button>
              ) : null}
              {shorten ? (
                <button
                  type="button"
                  onClick={() => shorten(target?.id ?? null)}
                  className="border-border bg-card hover:bg-accent focus-visible:outline-ring rounded-full border px-3 py-1 text-xs font-medium focus-visible:outline-2"
                >
                  Make it shorter
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      );
    }
    case "set_section_state":
      return line(`Couldn't mark ${box} ${input.state === "not_sure" ? "not sure" : "done"}.`, "You can mark it yourself from the box.");
    case "add_section":
      return line(
        `Couldn't add the section${typeof input.title === "string" && input.title.trim() ? ` ${input.title.trim()}` : ""}.`,
        "You can add it yourself with Add section.",
      );
    case "set_brief":
      return input.title
        ? line("Couldn't set the title.", "You can type it at the top of the brief.")
        : line("Couldn't set the summary.", "You can type it at the top of the brief.");
    case "read_attachment":
      return line("Couldn't read the file.", "You can say what is in it instead.");
    default:
      return null;
  }
}

/**
 * One chip under an assistant message, for a call that did what it was asked
 * (a failed one is a ToolFailure). A chip that names a box is a button
 * that opens it, when the page can (the owner's editor passes `onOpen`).
 */
function ToolChip({ part, sections, onOpen }: { part: ToolPart; sections: Section[]; onOpen?: (sectionId: string) => void }) {
  const name = part.type.slice("tool-".length);
  const input = (part.input ?? {}) as Record<string, unknown>;
  const output = (part.output ?? {}) as Record<string, unknown>;
  const base = "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px]";
  const tone = "bg-accent text-accent-foreground";
  const boxChip = (children: React.ReactNode) => {
    const target = sectionOf(sections, input.section);
    if (!onOpen || !target) return <span className={cn(base, tone)}>{children}</span>;
    return (
      <button
        type="button"
        onClick={() => onOpen(target.id)}
        title={`Open ${target.title}`}
        className={cn(base, tone, "focus-visible:outline-ring cursor-pointer underline-offset-2 hover:underline focus-visible:outline-2")}
      >
        {children}
      </button>
    );
  };

  switch (name) {
    case "write_section":
      return boxChip(
        <>
          <PenLine size={12} />
          {output.unchanged ? "Already in" : output.mode === "suggested" ? "Suggested a change to" : "Wrote"} {sectionTitle(sections, input.section)}
        </>,
      );
    case "set_section_state":
      return boxChip(
        <>
          <Check size={12} />
          Marked {sectionTitle(sections, input.section)} {input.state === "not_sure" ? "not sure" : "done"}
        </>,
      );
    case "add_section":
      return (
        <span className={cn(base, tone)}>
          <PenLine size={12} /> Added a section: {String(input.title ?? "")}
        </span>
      );
    case "set_brief":
      return (
        <span className={cn(base, tone)}>
          <Tag size={12} /> {input.title ? `Named it "${String(input.title)}"` : "Set the summary"}
        </span>
      );
    case "read_attachment":
      return (
        <span className={cn(base, tone)}>
          <FileText size={12} /> Read {String(output.filename ?? "a file")}
        </span>
      );
    case "show_mockup":
      return null; // rendered below by Mockup
    case "suggest_replies":
    case "offer":
      return null; // rendered by the answer card
    default:
      return null;
  }
}

export function ChatMessage({
  message,
  sections,
  streaming,
  onReact,
  latestMockupId,
  onOpenSection,
  canEdit = false,
  busy = false,
  onShorten,
}: {
  message: UIMessage;
  sections: Section[];
  streaming: boolean;
  onReact?: (text: string, data: { toolCallId: string } & Reactions) => void;
  latestMockupId?: string | null;
  /** Opens a box a chip names; without it the chips stay plain (the review pane). */
  onOpenSection?: (sectionId: string) => void;
  /** The person can edit this brief: a failed write shows them what to do next. */
  canEdit?: boolean;
  /** The chat is answering: the failed-write card's buttons hide until it is done. */
  busy?: boolean;
  /** Asks the AI for the box shorter, from a failed write's card. */
  onShorten?: (sectionId: string | null) => void;
}) {
  const isUser = message.role === "user";
  // A turn can carry two text parts (a lead-in beside the tool calls, then the
  // reply); they are separate paragraphs, never run together.
  const text = message.parts
    .filter((p): p is Part & { type: "text"; text: string } => p.type === "text")
    .map((p) => p.text.trim())
    .filter(Boolean)
    .join("\n\n");
  const action = message.parts.find((p) => p.type === "data-action") as { data?: { action?: string } } | undefined;
  // Turns from before the rename carry "dish" for what is now "example".
  const actionKey = action?.data?.action === "dish" ? "example" : action?.data?.action;
  const technique = actionKey ? TECHNIQUES.find((t) => t.key === actionKey) : null;
  const allTools = message.parts.filter(isTool).filter((p) => !["tool-suggest_replies", "tool-offer", "tool-show_mockup"].includes(p.type));
  // A call that failed beside one that did the same thing (a step-two repeat of
  // a step-one write, which never runs) is not news; the success's chip says it.
  const succeeded = new Set(allTools.filter((p) => !failedPart(p)).map(targetOf));
  const failures = allTools.filter((p) => failedPart(p) && !succeeded.has(targetOf(p)));
  const tools = allTools.filter((p) => !failedPart(p));

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="bg-primary text-primary-foreground max-w-[85%] rounded-2xl rounded-br-sm px-3.5 py-2 text-sm whitespace-pre-wrap">
          {technique ? <span className="mb-0.5 block text-[11px] opacity-80">Technique · {technique.label}</span> : null}
          {message.parts.some((p) => p.type === "data-answer") ? <span className="mb-0.5 block text-[11px] opacity-80">From the card</span> : null}
          {message.parts.some((p) => p.type === "data-mockup") ? (
            <span className="mb-0.5 block text-[11px] opacity-80">Reacting to the mock-up</span>
          ) : null}
          {message.parts.some((p) => p.type === "data-attachment") ? <span className="mb-0.5 block text-[11px] opacity-80">Attached a file</span> : null}
          {text}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <div className="bg-accent text-primary mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full">
        <Sparkles size={13} />
      </div>
      <div className="min-w-0 max-w-[90%]">
        {tools.length > 0 ? (
          <div className="mb-1 flex flex-wrap gap-1">
            {tools.map((p) => (
              <ToolChip key={p.toolCallId} part={p} sections={sections} onOpen={onOpenSection} />
            ))}
          </div>
        ) : null}
        {failures.length > 0 ? (
          <div className="mb-1.5 space-y-1.5">
            {failures.map((p) => (
              <ToolFailure key={p.toolCallId} part={p} sections={sections} canEdit={canEdit} busy={busy || streaming} onOpen={onOpenSection} onShorten={onShorten} />
            ))}
          </div>
        ) : null}
        {text ? <Prose text={text} /> : streaming ? <span className="text-muted-foreground text-sm">Thinking…</span> : null}
        {mockupsOf(message).map((v) => (
          <Mockup key={v.toolCallId} view={v} latest={v.toolCallId === latestMockupId} canReact={!!onReact && !streaming} onSend={(t, d) => onReact?.(t, d)} />
        ))}
      </div>
    </div>
  );
}
